import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@optools/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { cn } from '@optools/components/ui/utils';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import * as React from 'react';

export interface MultiSelectOption {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}

interface MultiSelectProps {
  className?: string;
  disabled?: boolean;
  id?: string;
  onChange: (selectedValues: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  selected: string[];
}

export function MultiSelect({
  className,
  disabled,
  id,
  onChange,
  options,
  placeholder,
  selected,
}: MultiSelectProps) {
  const selectedValues = new Set(selected);

  return (
    <Popover>
      <PopoverTrigger asChild className="overflow-x-auto">
        <Button
          className={cn(
            'relative transition-none [&_svg:not([class*="text-"])]:text-muted-foreground',
            { 'h-auto p-2': selectedValues.size },
            className,
          )}
          disabled={disabled}
          id={id}
          role="combobox"
          variant="outline">
          <span className="flex flex-1 justify-start">
            {selectedValues?.size > 0 ? (
              <div className="flex max-w-[90%] flex-wrap gap-2">
                {options
                  .filter((option) => selectedValues.has(option.value))
                  .map((option) => (
                    <Badge key={option.value} className="font-normal" variant="secondary">
                      {option.label}
                    </Badge>
                  ))}
              </div>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDownIcon className="absolute right-3 ml-2 size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-full min-w-(--radix-popper-anchor-width) p-0">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      onChange(filterValues.length ? filterValues : ([] as string[]));
                    }}>
                    <div
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible',
                      )}>
                      <CheckIcon className={cn('h-4 w-4')} />
                    </div>
                    {option.icon && <option.icon className="h-4 w-4 text-muted-foreground" />}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandList>
                    <CommandItem
                      className="justify-center text-center"
                      onSelect={() => onChange([])}>
                      Clear
                    </CommandItem>
                  </CommandList>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
